/**
 * The "Mark as tester" / "Remove tester" control on the admin user page.
 *
 * Task 7.11, spec 2026-09-22. The API is already audited (see
 * `backend/src/routes/admin-testers.ts` and `backend/src/index.ts` ~1771–1800):
 *
 *   POST   /v1/admin/testers          { user_id, reason } → 201 { user_id, is_tester: true }
 *   DELETE /v1/admin/testers/:userId  { reason }          → 200 { user_id, is_tester: false }
 *
 * Both 409 on an idempotent re-fire (already-tester / already-not-a-tester),
 * 400 on an empty reason, and both require permission `users.tester.toggle`
 * (Super Admin only on PROD). No backend change here — this file is only the
 * request building, reason validation and error-to-message mapping the page
 * needs, split out (like `pages/notifications/data.ts`) so a root jest test
 * can execute it: root jest's ts-jest config sets no `jsx` option, so a
 * `.tsx` import fails with `SyntaxError: Unexpected token '<'` — the rules
 * live here, and `show.tsx` holds only markup.
 */
import { adminFetch, AdminApiError } from '@/providers/admin-api';

/**
 * Written as a bare quoted literal on purpose (not built as
 * `` `${apiUrl}/v1/admin/testers` `` inline) so
 * `tests/unit/lint/admin-fetch-route-exists.test.ts` — which scans for
 * `['"\`](/v1/admin/...)['"\`]` — can resolve it against the route declared
 * in `backend/src/index.ts` (`path === '/v1/admin/testers'`). Reused for the
 * DELETE URL below, whose static prefix is the same route.
 */
export const TESTER_TOGGLE_PATH = '/v1/admin/testers';

export const TESTER_REASON_MAX_LENGTH = 500;

/**
 * What the modal tells the admin the flag does. The first version said only
 * "left out of member counts and of lifecycle email on production", and that
 * was false in the direction that matters: on production a tester also leaves
 * organic MATCHING and every scheduled reminder, including match reminders
 * (audit of all 34 `is_tester` readers, 2026-09-25). An admin who marked a
 * staff member on that sentence would have stopped their matches without
 * being told.
 *
 * Each effect is a claim about code elsewhere, and
 * `tests/unit/admin/users-tester-toggle.test.ts` ties every line to the code
 * that makes it true — change a reader and the build fails until this copy
 * changes with it. The list is for production only: on DEV and PREDEV the
 * scheduler and outbox do not exclude testers, and the fake pool serves them.
 */
export const TESTER_TOGGLE_LEAD =
  'A tester is a demo or review account. On production a tester:';

export const TESTER_TOGGLE_EFFECTS: readonly string[] = [
  'gets no new matches and is never offered to a member as a match (matches it already has continue)',
  'gets no scheduled reminders — no push, in-app card or email, match reminders included',
  'still gets messages sent the moment something happens, such as a mutual match, but one held back for quiet hours is dropped',
  'is left out of the dashboard and agent member counts (the Acquisition page and the daily CMO digest still count it)',
  'sees the in-app feedback button',
];

export const TESTER_TOGGLE_AUDIT_NOTE =
  'The change and your reason are written to the audit log.';

export type TesterToggleDirection = 'enable' | 'disable';

export interface TesterToggleResult {
  user_id: string;
  is_tester: boolean;
}

/**
 * Trims and validates a tester-toggle reason.
 * Returns `null` when valid, or the message to show under the field.
 */
export function validateTesterReason(reason: string): string | null {
  const trimmed = reason.trim();
  if (trimmed.length === 0) {
    return 'A reason is required.';
  }
  if (trimmed.length > TESTER_REASON_MAX_LENGTH) {
    return `The reason must be ${TESTER_REASON_MAX_LENGTH} characters or fewer.`;
  }
  return null;
}

export function isTesterReasonValid(reason: string): boolean {
  return validateTesterReason(reason) === null;
}

/** `POST /v1/admin/testers` — flips a user to `is_tester = true`. */
export async function enableTester(
  apiUrl: string,
  userId: string,
  reason: string,
): Promise<TesterToggleResult> {
  const res = await adminFetch(`${apiUrl}${TESTER_TOGGLE_PATH}`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, reason: reason.trim() }),
  });
  return (await res.json()) as TesterToggleResult;
}

/** `DELETE /v1/admin/testers/:userId` — flips a user to `is_tester = false`. */
export async function disableTester(
  apiUrl: string,
  userId: string,
  reason: string,
): Promise<TesterToggleResult> {
  const res = await adminFetch(
    `${apiUrl}${TESTER_TOGGLE_PATH}/${encodeURIComponent(userId)}`,
    {
      method: 'DELETE',
      body: JSON.stringify({ reason: reason.trim() }),
    },
  );
  return (await res.json()) as TesterToggleResult;
}

/**
 * Maps a caught error to the message the modal shows.
 *
 * The backend's 409 is the same `ConflictError` shape in both directions
 * ("User is already a tester or does not exist." / "User is not a tester or
 * does not exist.") — this page picks the direction-specific sentence the
 * brief asks for instead of surfacing that text. Everything other than
 * 409/403 falls back to the page's existing error pattern (`err.message`,
 * same as `handleSuspend`/`handleDelete`).
 */
export function describeTesterToggleError(
  err: unknown,
  direction: TesterToggleDirection,
): string {
  if (err instanceof AdminApiError) {
    if (err.status === 409) {
      return direction === 'enable'
        ? 'This account is already a tester.'
        : 'This account is not a tester.';
    }
    if (err.status === 403) {
      return 'Your role cannot change the tester flag.';
    }
  }
  if (err instanceof Error) {
    return err.message;
  }
  return direction === 'enable'
    ? 'Failed to mark user as tester'
    : 'Failed to remove tester status';
}
